import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import * as yup from "yup";
import { FormSelectAPI } from "../../components/FormReactSelect";
import { Input } from "../../components/inputs/Input";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { AxiosError } from "axios";
import { isNotEmpty } from "../../helpers/general";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  DEFAULT_PORTAL_PATHS,
  PORTAL_PATH_OPTIONS,
} from "../../helpers/constants";

interface Props {
  data?: Driver | User;
  role?: "user" | "driver";
  invalidateQuery?: () => void;
}

const validations = yup.object().shape({
  fullName: yup.string().required("Ce champ est obligatoire"),
  email: yup.string().required("Ce champ est obligatoire"),
  phone: yup.string().required("Ce champ est obligatoire"),
  birthday: yup.mixed().optional(),
  cityId: yup.mixed().nullable(),
  cinNumber: yup.string().optional(),
  isVerifiedPhone: yup.boolean().default(false),
  allowedPortalPaths: yup.array().of(yup.string().required()).optional(),
});

type FormInput = {
  fullName: string;
  email: string;
  phone: string;
  cinNumber?: string;
  birthday?: any;
  cityId?: any;
  isVerifiedPhone: boolean;
  allowedPortalPaths?: string[];
};

export default function GeneralInfoForm(props: Props) {
  const { data, role } = props;
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const isPartner = data?.role?.label === "Partner";
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(validations),
  });

  const selectedPaths = watch("allowedPortalPaths") ?? [];
  const customPaths = selectedPaths.filter(
    (p) => !PORTAL_PATH_OPTIONS.some((o) => o.value === p),
  );

  useEffect(() => {
    const defaultValues = {
      fullName: data?.fullName,
      email: data?.email,
      phone: data?.phone,
      cinNumber: data?.cinNumber,
      birthday: data?.birthday,
      cityId: data?.city,
      isVerifiedPhone: data?.isVerifiedPhone ?? false,
      allowedPortalPaths: isPartner
        ? ((data as any)?.allowedPortalPaths ?? DEFAULT_PORTAL_PATHS)
        : undefined,
    };
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (value) {
          if (key === "birthday") {
            setValue(
              key as any,
              new Date(value as string).toISOString().split("T")[0],
            );
          } else {
            setValue(key as any, value);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const updateProfile = useMutation((payload: FormInput) => {
    return apiService.MakePutRequest(
      `users/${data?.id}/driver`,
      payload,
      token,
    );
  });

  const updateProfileHandler = async (data: FormInput) => {
    try {
      await updateProfile.mutateAsync({
        ...data,
        cityId: data?.cityId?.id,
        isVerifiedPhone: data.isVerifiedPhone,
        ...(isPartner && {
          allowedPortalPaths: Array.from(
            new Set([...(data.allowedPortalPaths ?? [])]),
          ),
        }),
      });
      toastSuccess(general.fr.message.profileUpdated);
      props?.invalidateQuery?.();
    } catch (error) {
      let errorMessage = general.fr.message.operationFailed;
      if (error instanceof AxiosError) {
        const apiError = error.response?.data as VenezApiError;
        if (isNotEmpty(apiError?.message)) {
          errorMessage = apiError.message;
        }
      }
      toastError(errorMessage);
    }
  };

  const cityError = errors?.fullName?.message;

  return (
    <section
      className="p-5 my-3 d-flex flex-column align-items-center mx-auto border rounded-4"
      style={{
        background:
          data?.email === "guest-user@venez.ma"
            ? "radial-gradient(ellipse at center, white 0%, #f6b186 100%)"
            : "white",
      }}
    >
      <div className="d-flex flex-column align-items-center mx-auto p-4 rounded-4 bg-white">
        <form className="col-md-4 form">
          <div className="d-flex flex-column justify-content-center  py-2 align-items-center">
            <div className="position-relative">
              <img
                src={
                  (data?.profilePicture as string) ??
                  `https://ui-avatars.com/api/?name=${data?.fullName}`
                }
                alt=""
                width={200}
                height={200}
                className="rounded-circle border border-2 border-danger overflow-hidden bg-white object-fit-cover"
              />
            </div>
          </div>
        </form>
        <form
          onSubmit={handleSubmit(updateProfileHandler)}
          className="col-md-10 mt-2"
        >
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="fullName" className="d-block text-sm">
                Nom complet
              </label>
              <Input
                type="text"
                className="custom-input w-100 mt-1"
                name="fullName"
                placeholder="Nom complet"
                id="fullName"
                // defaultValue={data?.fullName}
                control={control}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="email" className="d-block text-sm">
                Email
              </label>
              <Input
                type="email"
                className="custom-input w-100 mt-1"
                name="email"
                placeholder="Email"
                id="email"
                // defaultValue={data?.email}
                control={control}
                disabled
                readOnly
              />
            </div>
            <div className="col-md-6 mb-3 position-relative">
              <label htmlFor="phone" className="d-block text-sm">
                Téléphone
              </label>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    id="phone"
                    placeholder="Téléphone"
                    value={field.value}
                    defaultCountry="MA"
                    international
                    required
                    className="phone-input custom-input w-100 mt-1 pe-5"
                    onChange={(value) => field.onChange(value ?? "")}
                  />
                )}
              />
              <span className="position-absolute top-50 end-0 me-4">
                {data?.isVerifiedPhone ? "✅" : "❌"}
              </span>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="birthday" className="d-block text-sm">
                Date de naissance
              </label>
              <Input
                type="date"
                className="custom-input"
                placeholder="Date"
                id="date"
                name="birthday"
                // defaultValue={
                //   data?.birthday
                //     ? new Date(data?.birthday).toISOString().split("T")[0]
                //     : ""
                // }
                control={control}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="isVerifiedPhone" className="d-block text-sm">
                Téléphone vérifié
              </label>
              <Controller
                name="isVerifiedPhone"
                control={control}
                render={({ field }) => (
                  <div
                    className="d-flex align-items-center justify-content-between border rounded-3 px-3 mt-1"
                    style={{ height: 46 }}
                  >
                    <span className="text-sm text-secondary">
                      {field.value ? "Vérifié" : "Non vérifié"}
                    </span>
                    <label className="switch mb-0">
                      <input
                        type="checkbox"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        id="isVerifiedPhone"
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                )}
              />
            </div>
            {isPartner && (
              <div className="col-12 mb-3">
                <div className="d-flex align-items-center justify-content-between">
                  <label className="d-block text-sm mb-1">
                    Pages visibles dans le portail partenaire
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm btn-link"
                    onClick={() =>
                      setValue("allowedPortalPaths", DEFAULT_PORTAL_PATHS)
                    }
                  >
                    Réinitialiser
                  </button>
                </div>

                <div className="row g-2 mt-1">
                  {PORTAL_PATH_OPTIONS.map((option) => (
                    <div className="col-md-6" key={option.value}>
                      <div className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                        <label
                          htmlFor={`portal-path-${option.value}`}
                          className="text-sm mb-0"
                        >
                          {option.label}{" "}
                        </label>
                        <label className="switch mb-0">
                          <input
                            type="checkbox"
                            id={`portal-path-${option.value}`}
                            checked={selectedPaths.includes(option.value)}
                            onChange={(e) =>
                              setValue(
                                "allowedPortalPaths",
                                e.target.checked
                                  ? [...selectedPaths, option.value]
                                  : selectedPaths.filter(
                                      (p) => p !== option.value,
                                    ),
                                { shouldDirty: true },
                              )
                            }
                          />
                          <span className="slider round"></span>
                        </label>
                      </div>
                    </div>
                  ))}

                  {customPaths.map((path) => (
                    <div className="col-md-6" key={path}>
                      <div className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                        <span className="text-sm">
                          {path}{" "}
                          <span className="badge bg-secondary ms-1">perso</span>
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0"
                          onClick={() =>
                            setValue(
                              "allowedPortalPaths",
                              selectedPaths.filter((p) => p !== path),
                              { shouldDirty: true },
                            )
                          }
                        >
                          Retirer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="col-md-6 mb-3">
              <label htmlFor="cityId" className="d-block text-sm mb-1">
                Ville
              </label>
              <Controller
                name="cityId"
                control={control}
                render={({ field }) => (
                  <FormSelectAPI
                    // name="City"
                    id="cityId"
                    isClearable={false}
                    getOptionLabel={(option: City) => option?.label}
                    getOptionValue={(option: City) => option.id}
                    isOptionSelected={(option: City, selectedValue: City[]) => {
                      const isSelected = option?.id === selectedValue?.[0]?.id;
                      return isSelected;
                    }}
                    url={{
                      path: "cities",
                    }}
                    {...field}
                    // defaultValue={data?.city}
                    required
                  />
                )}
              />
              {cityError && <p className="text-red">{cityError}</p>}
            </div>
            {role === "driver" && (
              <div className="col-md-6 mb-3">
                <label htmlFor="cin" className="d-block text-sm">
                  CIN
                </label>
                <Input
                  className="custom-input w-100 mt-1"
                  name="cinNumber"
                  placeholder="CIN"
                  id="cin"
                  control={control}
                />
              </div>
            )}
            {/* <div className="col-md-6 mb-3">
              <label className="d-block text-sm">Téléphone verifié</label>
              <p className="text mt-3 ms-3 text-sm text-secondary ">
                {data?.isVerifiedPhone ? "Oui" : "Non"}
              </p>
            </div> */}
          </div>

          <button
            disabled={updateProfile.isLoading}
            className="btn btn-md btn-danger rounded-sm my-2 text-white"
          >
            Mettre &agrave; jour les informations
          </button>
        </form>
      </div>
    </section>
  );
}
